import { useEffect, useMemo, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { completeCoffeeOrder } from "../../api";
import { formatHuf } from "./checkoutUtils";

function CheckoutPaySkeleton({ t, amountTotalHuf }) {
  const payLabel = amountTotalHuf
    ? t.payButton.replace("{amount}", formatHuf(amountTotalHuf))
    : t.loading;

  return (
    <section
      className="checkout-pay-card checkout-pay-card--skeleton"
      aria-busy="true"
      aria-labelledby="checkout-pay-title"
    >
      <h3 id="checkout-pay-title" className="checkout-payment-card-title">
        {t.paymentTitle}
      </h3>
      <p className="checkout-step-hint">{t.paymentHint}</p>

      <div className="checkout-pay-skeleton-fields" aria-hidden="true">
        <div className="checkout-pay-skeleton-label" />
        <div className="checkout-pay-skeleton-input checkout-pay-skeleton-input--lg" />
        <div className="checkout-pay-skeleton-row">
          <div className="checkout-pay-skeleton-col">
            <div className="checkout-pay-skeleton-label" />
            <div className="checkout-pay-skeleton-input" />
          </div>
          <div className="checkout-pay-skeleton-col">
            <div className="checkout-pay-skeleton-label" />
            <div className="checkout-pay-skeleton-input" />
          </div>
        </div>
        <div className="checkout-pay-skeleton-label" />
        <div className="checkout-pay-skeleton-input" />
      </div>

      <button type="button" className="checkout-pay-submit" disabled>
        {payLabel}
      </button>
    </section>
  );
}

function CheckoutPayForm({
  t,
  lang,
  billing,
  customer,
  amountTotalHuf,
  onSuccess,
  onError,
  onElementReady,
  elementReady,
  returnQuery = "",
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const payLabel = t.payButton.replace("{amount}", formatHuf(amountTotalHuf));
  const canPay = Boolean(stripe && elements && elementReady && !submitting);

  async function handleSubmit(event) {
    event.preventDefault();

    if (!stripe || !elements || !elementReady) {
      return;
    }

    setSubmitting(true);
    onError("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/checkout/confirm?lang=${lang === "en" ? "en" : "hu"}${returnQuery}`,
        payment_method_data: {
          billing_details: {
            name: billing.name.trim(),
            email: billing.email.trim(),
            phone: customer.phone.trim(),
            address: {
              line1: [billing.street, billing.houseNumber].filter(Boolean).join(" ").trim(),
              city: billing.city.trim(),
              postal_code: billing.postalCode.trim(),
              country: "HU",
            },
          },
        },
      },
      redirect: "if_required",
    });

    if (error) {
      onError(error.message || t.paymentError);
      setSubmitting(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      try {
        await completeCoffeeOrder(paymentIntent.id);
      } catch (completeError) {
        console.error("Order email fulfillment failed:", completeError);
      }
      onSuccess();
      return;
    }

    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit} className="checkout-pay-form">
      <section className="checkout-pay-card" aria-labelledby="checkout-pay-title">
        <h3 id="checkout-pay-title" className="checkout-payment-card-title">
          {t.paymentTitle}
        </h3>
        <p className="checkout-step-hint">{t.paymentHint}</p>

        <div
          className={[
            "checkout-payment-element-wrap",
            elementReady ? "is-ready" : "is-loading",
          ].join(" ")}
        >
          {!elementReady ? (
            <div className="checkout-pay-skeleton-fields" aria-hidden="true">
              <div className="checkout-pay-skeleton-label" />
              <div className="checkout-pay-skeleton-input checkout-pay-skeleton-input--lg" />
              <div className="checkout-pay-skeleton-row">
                <div className="checkout-pay-skeleton-col">
                  <div className="checkout-pay-skeleton-label" />
                  <div className="checkout-pay-skeleton-input" />
                </div>
                <div className="checkout-pay-skeleton-col">
                  <div className="checkout-pay-skeleton-label" />
                  <div className="checkout-pay-skeleton-input" />
                </div>
              </div>
              <div className="checkout-pay-skeleton-label" />
              <div className="checkout-pay-skeleton-input" />
            </div>
          ) : null}

          <PaymentElement
            options={{ layout: "tabs" }}
            onReady={() => onElementReady()}
          />
        </div>

        <button type="submit" className="checkout-pay-submit" disabled={!canPay}>
          {submitting || !elementReady ? t.loading : payLabel}
        </button>
      </section>
    </form>
  );
}

function CheckoutPaymentElements({
  t,
  lang,
  clientSecret,
  stripePromise,
  billing,
  customer,
  amountTotalHuf,
  onSuccess,
  onError,
  returnQuery,
}) {
  const [elementReady, setElementReady] = useState(false);

  useEffect(() => {
    setElementReady(false);
  }, [clientSecret]);

  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#9a6840",
          colorText: "#2a1b16",
          colorTextSecondary: "#7a6558",
          colorDanger: "#b42318",
          borderRadius: "12px",
          fontFamily: "Inter, Segoe UI, Arial, sans-serif",
          fontSizeBase: "15px",
          fontWeightNormal: "400",
          fontWeightMedium: "500",
          fontWeightBold: "600",
        },
        rules: {
          ".Label": {
            fontFamily: "Inter, Segoe UI, Arial, sans-serif",
            fontSize: "13px",
            fontWeight: "500",
          },
          ".Input": {
            fontFamily: "Inter, Segoe UI, Arial, sans-serif",
            border: "1px solid rgba(122, 79, 44, 0.18)",
            boxShadow: "none",
          },
          ".Input:focus": {
            border: "1px solid rgba(154, 104, 64, 0.55)",
            boxShadow: "0 0 0 1px rgba(154, 104, 64, 0.25)",
          },
          ".Tab": {
            fontFamily: "Inter, Segoe UI, Arial, sans-serif",
          },
        },
      },
      locale: lang === "en" ? "en" : "hu",
    }),
    [clientSecret, lang],
  );

  return (
    <Elements
      key={clientSecret}
      stripe={stripePromise}
      options={elementsOptions}
    >
      <CheckoutPayForm
        t={t}
        lang={lang}
        billing={billing}
        customer={customer}
        amountTotalHuf={amountTotalHuf}
        onSuccess={onSuccess}
        onError={onError}
        elementReady={elementReady}
        onElementReady={() => setElementReady(true)}
        returnQuery={returnQuery}
      />
    </Elements>
  );
}

function CheckoutPaymentStep({
  t,
  lang,
  billing,
  clientSecret,
  stripePromise,
  paymentLoading,
  customer,
  amountTotalHuf,
  onSuccess,
  onError,
  returnQuery = "",
}) {
  if (clientSecret && stripePromise) {
    return (
      <CheckoutPaymentElements
        t={t}
        lang={lang}
        clientSecret={clientSecret}
        stripePromise={stripePromise}
        billing={billing}
        customer={customer}
        amountTotalHuf={amountTotalHuf}
        onSuccess={onSuccess}
        onError={onError}
        returnQuery={returnQuery}
      />
    );
  }

  return <CheckoutPaySkeleton t={t} amountTotalHuf={amountTotalHuf} />;
}

export default CheckoutPaymentStep;
